import React, { useEffect, useState } from 'react';
import PartnerLayout from '../../components/layout/PartnerLayout';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Users, Search, UserPlus, Eye, FileText } from 'lucide-react';
import { partnerAPI } from '../../services/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export default function PartnerStudents() {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchStudents();
  }, [page]);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const response = await partnerAPI.getStudents({ page, limit: 20 });
      setStudents(response.data);
      setPagination(response.pagination);
    } catch (error) {
      toast.error('Failed to fetch students');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-700',
      inactive: 'bg-gray-100 text-gray-700',
      completed: 'bg-blue-100 text-blue-700'
    };
    return <Badge className={styles[status] || 'bg-gray-100 text-gray-700'}>{status}</Badge>;
  };

  const filteredStudents = students.filter(s => {
    if (!search) return true;
    const name = `${s.user?.firstName || ''} ${s.user?.lastName || ''}`.toLowerCase();
    return name.includes(search.toLowerCase()) || (s.user?.email || '').toLowerCase().includes(search.toLowerCase());
  });

  return (
    <PartnerLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">My Students</h1>
            <p className="mt-1 text-slate-600">Students you manage as a Fly8 Partner</p>
          </div>
          <Button
            onClick={() => navigate('/partner/add-student')}
            className="partner-primary-btn rounded-2xl"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Add Student
          </Button>
        </div>

        {/* Search */}
        <div className="partner-card rounded-[24px] p-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8D68E8]" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-2xl border border-[#A87DFA]/18 bg-white/70 py-3 pl-10 pr-4 text-sm"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredStudents.length === 0 ? (
          <Card className="partner-card rounded-[28px] p-12 text-center">
            <Users className="mx-auto mb-3 h-12 w-12 text-[#A87DFA]" />
            <h3 className="text-lg font-semibold text-slate-900">No students yet</h3>
            <p className="mb-4 mt-1 text-sm text-slate-600">Create your first student profile to get started</p>
            <Button
              onClick={() => navigate('/partner/add-student')}
              className="partner-primary-btn rounded-2xl"
            >
              <UserPlus className="w-4 h-4 mr-2" /> Add Student
            </Button>
          </Card>
        ) : (
          <div className="grid gap-4">
            {filteredStudents.map((student) => (
              <Card key={student.studentId} className="partner-card partner-card-hover rounded-[26px] p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[linear-gradient(135deg,#A87DFA,#8E66F1)] text-sm font-semibold text-white shadow-[0_12px_30px_rgba(168,125,250,0.22)]">
                      {student.user?.firstName?.[0]}{student.user?.lastName?.[0]}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">
                        {student.user?.firstName} {student.user?.lastName}
                      </p>
                      <p className="text-sm text-slate-500">{student.user?.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(student.status)}
                    {student.country && (
                      <span className="text-xs text-slate-500">{student.country}</span>
                    )}
                    <button
                      onClick={() => navigate(`/partner/students/${student.studentId}`)}
                      className="rounded-2xl border border-[#A87DFA]/14 bg-white/40 p-2 transition-colors hover:bg-white/70"
                      title="View Details"
                    >
                      <Eye className="w-4 h-4 text-[#7A55DF]" />
                    </button>
                    <button
                      onClick={() => navigate(`/partner/service-requests?studentId=${student.studentId}`)}
                      className="rounded-2xl border border-[#A87DFA]/14 bg-white/40 p-2 transition-colors hover:bg-white/70"
                      title="Apply for Service"
                    >
                      <FileText className="w-4 h-4 text-[#7A55DF]" />
                    </button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="partner-secondary-btn rounded-2xl px-3 py-1.5 text-sm disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm text-slate-600">Page {page} of {pagination.totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
              disabled={page === pagination.totalPages}
              className="partner-secondary-btn rounded-2xl px-3 py-1.5 text-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </PartnerLayout>
  );
}
